import { formatGbToUserNumber } from '@fd/common/biz/formatBytesToGb';
import {
  ProxyTypeName,
  getProxyTypeMetaByName,
} from '@fd/common/biz/proxyType';
import { useTrafficGbAvailableByProxyType } from '@/hooks/useTrafficAvailableByProxyType';
import useProxyPoolCreationForm from '@/store/proxyPoolCreationFormStore';
import clsx from 'clsx';
import { LucideCheck } from 'lucide-react';
import { NavLink } from 'react-router-dom';

export default function ProxyPoolTypeSelector() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-[1px] bg-gray-200 rounded">
      <ProxyPoolTypeOption
        optionProxyType={'residential'}
        description={
          <div>
            <span>Best for:</span>
            <ul>
              <li>•︎ Generate leads</li>
              <li>•︎ Run ads</li>
              <li>•︎ Scrape data</li>
            </ul>
          </div>
        }
      />
      <ProxyPoolTypeOption
        optionProxyType={'mobile'}
        description={
          <div>
            <span>Best for:</span>
            <ul>
              <li>•︎ Manage multiple accounts</li>
              <li>•︎ Protect your privacy</li>
              <li>•︎ Perform freelance jobs</li>
            </ul>
          </div>
        }
      />
      <ProxyPoolTypeOption
        optionProxyType={'datacenter'}
        description={
          <div>
            <span>Best for:</span>
            <ul>
              <li>•︎ Bypass geo-blocks</li>
              <li>•︎ Monitor prices</li>
            </ul>
          </div>
        }
      />
    </div>
  );
}

function ProxyPoolTypeOption({
  optionProxyType,
  description,
  readOnly = false,
}: {
  optionProxyType: ProxyTypeName;
  description?: React.ReactNode;
  readOnly?: boolean;
}) {
  const { proxyType, setProxyType } = useProxyPoolCreationForm();
  const availableTrafficGb = useTrafficGbAvailableByProxyType(optionProxyType);
  const isAvailable =
    availableTrafficGb !== undefined && availableTrafficGb > 0;

  const handleClick = () => {
    setProxyType(optionProxyType);
  };

  const isActive = optionProxyType === proxyType;
  const proxyTypeMeta = getProxyTypeMetaByName(optionProxyType);

  return (
    <div
      className={clsx({
        'flex p-6 ring-2 -ring-offset-1': 1,
        'cursor-pointer ': !readOnly,
        '': readOnly,
        'first:rounded-l last:rounded-r': 1,
        '-ring-offset-2 ': 1,
        'outline-gray-200 ring-transparent hover:rounded hover:outline-transparent hover:ring-indigo-500/70 hover:z-10 bg-gray-50':
          !isActive,
        'outline-transparent ring-indigo-500 z-20 rounded bg-white': isActive,
      })}
      onClick={handleClick}
    >
      <div className="flex-1 flex flex-col justify-between">
        <div>
          <span className="font-semibold text-gray-800">
            {proxyTypeMeta.title}
          </span>
          <div className="pt-2">{description || proxyTypeMeta.description}</div>
        </div>
        {isAvailable && (
          <div className="mt-2">
            <NavLink
              to={`/proxies/buy?type=${optionProxyType}`}
              className="inline-block py-2 pl-2 pr-3 text-sm leading-4 rounded-full bg-teal-100 font-bold text-teal-500 hover:bg-teal-200 hover:text-teal-600"
            >
              <LucideCheck className="inline-block h-4 -mt-[1.5px] stroke-[3px] " />{' '}
              Available {formatGbToUserNumber(availableTrafficGb)}GB
            </NavLink>
          </div>
        )}
        {isActive && availableTrafficGb === 0 && (
          <div className="mt-2">
            <NavLink
              to={`/proxies/buy?type=${optionProxyType}`}
              className="inline-block py-2 px-3 text-sm leading-4 rounded-full bg-indigo-100 font-bold text-indigo-500 hover:bg-indigo-200"
            >
              Buy to create
            </NavLink>
          </div>
        )}
      </div>
      <proxyTypeMeta.Icon
        className={clsx({
          'stroke-gray-200': !isActive,
          'stroke-indigo-600': isActive,
        })}
      />
    </div>
  );
}
